import React from 'react'
import {toAbsoluteUrl} from '../../../_metronic/helpers'

/** Neutral "no image" placeholder shipped in public/media (already used elsewhere in the portal). */
const PLACEHOLDER = '/media/logos/img-placeholder.png'

/**
 * True when a catalog image URL carries no actual image: falsy, or just the API/CDN base ending in the
 * bare `image/` segment with nothing after it (e.g. `https://www.felo.one/image/`). The partner-portal
 * API returns exactly that for rows that don't have an image yet (pending add-product requests, etc.),
 * and rendering it as an `<img>` shows a broken-image icon.
 */
export function hasNoImage(src?: string | null): boolean {
  if (!src) return true
  return /\/image\/?$/i.test(src.trim())
}

type Props = Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src'> & {src?: string | null}

/**
 * A product thumbnail that degrades gracefully: an empty image path (see {@link hasNoImage}) or a load
 * error swaps in the neutral placeholder instead of a broken `<img>`. Drop-in for the many product
 * tables that render `<img src={item.image} className='table-img'>` — the backend now returns
 * host-correct (per-tenant) URLs, so the only remaining breakage is genuinely-missing images.
 */
export const ProductThumb: React.FC<Props> = ({src, className = 'table-img', alt = '', ...rest}) => {
  const placeholder = toAbsoluteUrl(PLACEHOLDER)
  const initial = hasNoImage(src) ? placeholder : (src as string)
  return (
    <img
      src={initial}
      className={className}
      alt={alt}
      {...rest}
      onError={(e) => {
        // Guard against an infinite onError loop if the placeholder itself ever fails.
        const img = e.currentTarget
        if (img.dataset.fallback !== '1') {
          img.dataset.fallback = '1'
          img.src = placeholder
        }
      }}
    />
  )
}

export default ProductThumb
